import { useTranslation } from "react-i18next";
import { IoArrowBackOutline, IoArrowRedoSharp, IoSyncOutline, IoMapOutline, IoPhonePortraitOutline, IoLogoWhatsapp, IoStorefrontOutline } from "react-icons/io5";
import { MdLocalShipping } from "react-icons/md";
import { FaQuoteLeft } from "react-icons/fa";
import { Link } from "react-router-dom";
import Footer from "./components/Footer";

export default function HolaReparto() {
    const { t } = useTranslation();

    return (
        <div className="bg-white text-darkBlue min-h-screen flex flex-col font-sans">
            {/* Hero Section */}
            <div className="relative w-full overflow-hidden bg-darkBlue h-[60vh] md:h-[70vh] flex flex-col">
                {/* Background Image Container shifted to the right */}
                <div 
                    className="absolute inset-y-0 right-0 w-[65%] z-0"
                    style={{ 
                        backgroundImage: "url('projects/holareparto.webp')",
                        backgroundSize: 'cover',
                        backgroundPosition: 'right center',
                        backgroundRepeat: 'no-repeat'
                    }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-darkBlue via-darkBlue/85 to-darkBlue/60"></div>
                </div>
                {/* Extra solid overlay for the left side text area */}
                <div className="absolute inset-y-0 left-0 w-[40%] bg-darkBlue z-0"></div>

                {/* Content Overlay */}
                <div className="relative z-10 mx-auto px-5 md:px-0 md:w-[90%] lg:w-[80%] flex flex-col h-full">
                    <header className="flex min-h-[12vh] items-center py-5">
                        <Link to="/" className="flex items-center gap-2 text-white hover:text-cyan font-bold transition duration-300">
                            <IoArrowBackOutline size="1.2em" />
                            {t("home")}
                        </Link>
                    </header>
                    
                    <div className="flex flex-col justify-center flex-grow pb-16">
                        <h1 className="font-extrabold text-5xl md:text-7xl lg:text-8xl text-white mb-4 tracking-tight">
                            Hola Reparto
                        </h1>
                        <p className="text-xl md:text-2xl lg:text-3xl font-semibold text-cyan opacity-90 max-w-3xl leading-snug">
                            {t("HolaRepartoSubtitle")}
                        </p>
                        <div className="mt-6 flex items-center gap-2 md:gap-3 w-fit bg-white/10 border border-white/20 backdrop-blur-sm px-3 py-1.5 md:px-4 md:py-2.5 rounded-full">
                            <span className="text-white/70 text-[10px] md:text-xs font-semibold uppercase tracking-widest">{t("builtWithAI")}</span>
                            <div className="flex items-center gap-1.5 md:gap-2">
                                {["claude.svg", "gemini.svg", "antigravity.svg"].map(logo => (
                                    <div key={logo} className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-white/15 p-0.5 flex items-center justify-center">
                                        <img src={`/ai-tools/${logo}`} alt="" className="w-full h-full object-contain" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Main Content */}
            <main className="flex-grow pt-16 pb-24">
                <div className="mx-auto px-5 md:w-[90%] lg:w-[80%]">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
                        {/* Left Column: Content */}
                        <div className="lg:col-span-2 flex flex-col gap-16">
                            {/* Overview */}
                            <section>
                                <h2 className="font-bold text-2xl md:text-3xl mb-6 text-darkBlue border-b-4 border-cyan w-fit pb-1">{t("overview")}</h2>
                                <p className="text-base md:text-lg leading-relaxed opacity-80 whitespace-pre-wrap text-justify">
                                    {t("HolaReparto")}
                                </p>
                            </section>

                            {/* The Challenge */}
                            <section>
                                <h2 className="font-bold text-2xl md:text-3xl mb-6 text-darkBlue border-b-4 border-cyan w-fit pb-1">{t("holaRepartoChallengeTitle")}</h2>
                                <div className="flex flex-col gap-4">
                                    <p className="text-base md:text-lg leading-relaxed opacity-80 whitespace-pre-wrap text-justify">
                                        {t("holaRepartoChallengeP1")}
                                    </p>
                                    <p className="text-base md:text-lg leading-relaxed opacity-80 whitespace-pre-wrap text-justify">
                                        {t("holaRepartoChallengeP2")}
                                    </p>
                                </div>
                            </section>

                            {/* Technical Solution */}
                            <section>
                                <h2 className="font-bold text-2xl md:text-3xl mb-6 text-darkBlue border-b-4 border-cyan w-fit pb-1">{t("holaRepartoSolutionTitle")}</h2>
                                <p className="text-base md:text-lg leading-relaxed opacity-80 mb-8">
                                    {t("holaRepartoSolutionP1")}
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {[
                                        { icon: <IoSyncOutline size="1.5em" />, title: "holaRepartoSolutionItem1Title", desc: "holaRepartoSolutionItem1P" },
                                        { icon: <IoMapOutline size="1.5em" />, title: "holaRepartoSolutionItem2Title", desc: "holaRepartoSolutionItem2P" },
                                        { icon: <IoPhonePortraitOutline size="1.5em" />, title: "holaRepartoSolutionItem3Title", desc: "holaRepartoSolutionItem3P" },
                                        { icon: <IoLogoWhatsapp size="1.5em" />, title: "holaRepartoSolutionItem4Title", desc: "holaRepartoSolutionItem4P" }
                                    ].map((item, i) => (
                                        <div key={i} className="bg-darkBlue/5 p-6 rounded-2xl border border-darkBlue/10 hover:border-cyan/50 transition-colors">
                                            <div className="text-cyan mb-4">{item.icon}</div>
                                            <h3 className="font-bold text-lg mb-2 text-darkBlue">{t(item.title)}</h3>
                                            <p className="text-sm md:text-base opacity-80 leading-relaxed text-justify">{t(item.desc)}</p>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {/* AI-Assisted Development */}
                            <section>
                                <h2 className="font-bold text-2xl md:text-3xl mb-6 text-darkBlue border-b-4 border-cyan w-fit pb-1">{t("holaRepartoAiTitle")}</h2>
                                <p className="text-base md:text-lg leading-relaxed opacity-80 mb-8">
                                    {t("holaRepartoAiP1")}
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {[
                                        { logo: "claude.svg",      title: "holaRepartoAiClaudeTitle",      desc: "holaRepartoAiClaudeP" },
                                        { logo: "gemini.svg",      title: "holaRepartoAiGeminiTitle",      desc: "holaRepartoAiGeminiP" },
                                        { logo: "antigravity.svg", title: "holaRepartoAiAntigravityTitle", desc: "holaRepartoAiAntigravityP" }
                                    ].map((item, i) => (
                                        <div key={i} className="bg-darkBlue/5 p-6 rounded-2xl border border-darkBlue/10 hover:border-cyan/50 transition-colors flex flex-col gap-3">
                                            <div className="bg-cyan/10 p-3 rounded-xl w-fit">
                                                <img src={`/ai-tools/${item.logo}`} alt="" className="w-9 h-9" />
                                            </div>
                                            <h3 className="font-bold text-xl text-darkBlue">{t(item.title)}</h3>
                                            <p className="text-sm md:text-base opacity-80 leading-relaxed flex-grow">{t(item.desc)}</p>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {/* Implementation & Feedback */}
                            <section>
                                <h2 className="font-bold text-2xl md:text-3xl mb-6 text-darkBlue border-b-4 border-cyan w-fit pb-1">{t("holaRepartoImplementationTitle")}</h2>
                                <p className="text-base md:text-lg leading-relaxed opacity-80 mb-8">
                                    {t("holaRepartoImplementationP1")}
                                </p>
                                <div className="flex flex-col gap-8">
                                    <div className="flex gap-6 items-start">
                                        <div className="bg-cyan/10 p-3 rounded-xl text-cyan shrink-0">
                                            <IoStorefrontOutline size="1.5em" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-xl mb-2 text-darkBlue">{t("holaRepartoImplementationSub1Title")}</h3>
                                            <p className="text-base opacity-80 leading-relaxed text-justify">{t("holaRepartoImplementationSub1P")}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-6 items-start">
                                        <div className="bg-cyan/10 p-3 rounded-xl text-cyan shrink-0">
                                            <MdLocalShipping size="1.5em" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-xl mb-2 text-darkBlue">{t("holaRepartoImplementationSub2Title")}</h3>
                                            <p className="text-base opacity-80 leading-relaxed text-justify">{t("holaRepartoImplementationSub2P")}</p>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Results */}
                            <section className="bg-darkBlue text-white p-8 md:p-12 rounded-[2rem] shadow-xl relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-cyan/20 blur-3xl -mr-16 -mt-16"></div>
                                <h2 className="font-bold text-2xl md:text-3xl mb-6 text-white border-b-4 border-cyan w-fit pb-1">{t("holaRepartoResultsTitle")}</h2>
                                <p className="text-base md:text-lg leading-relaxed opacity-90 text-justify">
                                    {t("holaRepartoResultsP1")}
                                </p>
                            </section>

                            {/* Testimonial Section */}
                            <section className="bg-cyan/5 border-l-4 border-cyan p-6 md:p-8 rounded-tr-2xl rounded-br-2xl w-fit max-w-2xl">
                                <div className="flex flex-col gap-4">
                                    <div className="text-cyan opacity-40">
                                        <FaQuoteLeft size="2em" />
                                    </div>
                                    <p className="text-lg md:text-xl italic font-medium text-darkBlue/90 leading-relaxed">
                                        "{t("holaRepartoTestimonialText")}"
                                    </p>
                                    <div className="flex items-center gap-3">
                                        <div className="w-6 h-px bg-cyan"></div>
                                        <span className="text-xs font-bold uppercase tracking-widest text-cyan">{t("holaRepartoTestimonialTitle")}</span>
                                    </div>
                                </div>
                            </section>

                            {/* Multi-tenant preview */}
                            <section className="bg-darkBlue text-white p-8 md:p-12 rounded-[2rem] border border-cyan/40 shadow-xl relative overflow-hidden">
                                <div className="absolute bottom-0 left-0 w-40 h-40 bg-cyan/20 blur-3xl -ml-20 -mb-20"></div>
                                <div className="relative">
                                    <span className="text-cyan text-xs font-bold uppercase tracking-[0.2em]">{t("holaRepartoPreviewLabel")}</span>
                                    <h2 className="font-bold text-2xl md:text-3xl mt-3 mb-5 text-white">{t("holaRepartoPreviewTitle")}</h2>
                                    <p className="text-base md:text-lg leading-relaxed text-white/85 max-w-3xl mb-8">
                                        {t("holaRepartoPreviewP")}
                                    </p>
                                    <a href="https://holareparto-demo.vercel.app" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-md bg-cyan px-4 py-2.5 text-xs font-bold text-darkBlue transition hover:bg-white">
                                        {t("holaRepartoPreviewBtn").toUpperCase()}
                                        <IoArrowRedoSharp size="0.9em" />
                                    </a>
                                </div>
                            </section>
                        </div>

                        {/* Right Column: Sidebar */}
                        <div className="flex flex-col gap-8">
                            <div className="sticky top-8 flex flex-col gap-8">
                                <div className="bg-darkBlue/5 p-6 md:p-8 rounded-3xl border border-darkBlue/10 shadow-sm">
                                    <h2 className="font-bold text-xl md:text-2xl mb-4 md:mb-6 text-darkBlue">{t("techStack")}</h2>
                                    <div className="flex flex-wrap gap-2 md:gap-2.5">
                                        {["Next.js", "React", "Capacitor", "Supabase", "Tailwind CSS", "Mapbox", "GraphHopper", "Fenicio API", "Claude", "Gemini", "Antigravity IDE"].map(tech => (
                                            <span key={tech} className="bg-white px-3 py-1.5 md:px-4 md:py-2 border border-darkBlue/10 rounded-lg md:rounded-xl text-xs md:text-sm font-bold shadow-sm text-darkBlue transition hover:scale-105 cursor-default hover:text-cyan">
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
}
